import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import AmCharts from '@amcharts/amcharts3-react/index';
import {useTheme} from '@material-ui/core/styles';
import {
  Typography,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Table,
  Paper,
  Checkbox,
  Tooltip,
  IconButton,
  Box,
  Collapse,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  TextField,
} from '@material-ui/core';
import {ExpandLess, ExpandMore} from '@material-ui/icons';
import moment from 'moment';
import millify from 'millify';

// Custom hooks and utilities
import useStickyGraph from './hooks/useStickyGraph';
import {useTableScroll} from './hooks/useScrollPosition';
import useColumnCarousel from './hooks/useColumnCarousel';
import useStackedReportSearch from './hooks/useStackedReportSearch';
import {useStyles} from './styles/stackedReportStyles';
import {
  getStructuredFixedGraphDataForTheDay,
  getAvailableSources,
  getAvailableSourcesSorted,
  getStructuredDynamicGraphDataForTheDay,
  getSplitModeConfig,
  getTotals,
  sortedDaysData,
  fillMissingDates,
  applyNetPrebidResellerRevenue,
} from './helpers/dataHelpers';
import {
  prebidSourcesConfig,
  demandSourcesConfig,
  directSourcesConfig,
  deviceSourcesConfig,
  countrySourcesConfig,
  createMetricsConfig,
  graphSettings,
  getStackedGraphLayoutHeights,
} from './helpers/configs';
import {createBalloonFunction, getValueAxisConfig, getGraphsConfig} from './helpers/graphHelpers';
import usePanelChangeGuides from './hooks/usePanelChangeGuides';

// Subcomponents
import {SourceRow, TableTotals, MetricHeader, StyledTableCell} from './subcomponents';
import AnalyticsFilters from './subcomponents/AnalyticsFilters';
import CarouselNavigationCell from './subcomponents/CarouselNavigationCell';
import PanelChangeDetailPanel from './subcomponents/PanelChangeDetailPanel';
import PanelChangeGuideStrips from './subcomponents/PanelChangeGuideStrips';
import SspTypeChangePopup from './subcomponents/SspTypeChangePopup';
import {formatSspTypeChangeBadgeTooltip} from './helpers/panelChangeGuides';

// Global store
import useStore from '../../../store/store';
import usePersistentStore from '../../../store/persistentStore';

// PricesReport component
import PricesReport from '../pricesReport/PricesReport';

const buildDemandData = ({
  data,
  period,
  disabledSources,
  sourcesConfig,
  metricsConfig,
  sortByKey,
  sortOrder,
  type,
  prebidSplitMode = 'serverClient',
  directSourcesSet = new Set(),
}) => {
  if (!data || !Array.isArray(data) || data.length === 0) return null;

  let graphData = [];
  let graphDataNonS2S = [];
  let graphDataS2S = [];
  let graphDataDirect = [];
  let graphDataReseller = [];

  let availableSources = getAvailableSources(data, sourcesConfig, metricsConfig);

  if (availableSources.length === 0) return null;

  const filledData = fillMissingDates(data, period);

  filledData.forEach(day => {
    let structuredFixedDataFoTheDay = getStructuredFixedGraphDataForTheDay(day, metricsConfig);

    let daysData = {
      ...getStructuredDynamicGraphDataForTheDay(
        day,
        availableSources,
        metricsConfig,
        disabledSources,
        'grouped',
        prebidSplitMode,
        directSourcesSet
      ),
      ...structuredFixedDataFoTheDay,
    };

    let daysDataNonS2S = {
      ...getStructuredDynamicGraphDataForTheDay(
        day,
        availableSources,
        metricsConfig,
        disabledSources,
        'Non-S2S',
        prebidSplitMode,
        directSourcesSet
      ),
      ...structuredFixedDataFoTheDay,
    };

    let daysDataS2S = {
      ...getStructuredDynamicGraphDataForTheDay(
        day,
        availableSources,
        metricsConfig,
        disabledSources,
        'S2S',
        prebidSplitMode,
        directSourcesSet
      ),
      ...structuredFixedDataFoTheDay,
    };

    let daysDataDirect = {
      ...getStructuredDynamicGraphDataForTheDay(
        day,
        availableSources,
        metricsConfig,
        disabledSources,
        'Direct',
        prebidSplitMode,
        directSourcesSet
      ),
      ...structuredFixedDataFoTheDay,
    };

    let daysDataReseller = {
      ...getStructuredDynamicGraphDataForTheDay(
        day,
        availableSources,
        metricsConfig,
        disabledSources,
        'Reseller',
        prebidSplitMode,
        directSourcesSet
      ),
      ...structuredFixedDataFoTheDay,
    };

    graphData.push(sortedDaysData(daysData, sortOrder));
    graphDataNonS2S.push(sortedDaysData(daysDataNonS2S, sortOrder));
    graphDataS2S.push(sortedDaysData(daysDataS2S, sortOrder));
    graphDataDirect.push(sortedDaysData(daysDataDirect, sortOrder));
    graphDataReseller.push(sortedDaysData(daysDataReseller, sortOrder));
  });

  let totals = getTotals(
    graphData,
    availableSources,
    metricsConfig,
    [],
    prebidSplitMode,
    directSourcesSet
  );
  let totalsOfEnabledSources = getTotals(
    graphData,
    availableSources,
    metricsConfig,
    disabledSources,
    prebidSplitMode,
    directSourcesSet
  );

  let availableSourcesSorted = getAvailableSourcesSorted(
    metricsConfig,
    totals,
    availableSources,
    sortByKey,
    sortOrder,
    type
  );

  return {
    totals,
    totalsOfEnabledSources,
    graphData,
    graphDataNonS2S,
    graphDataS2S,
    graphDataDirect,
    graphDataReseller,
    availableSources,
    availableSourcesSorted,
  };
};

const StackedReportGraph = ({
  data,
  comparisonData = null,
  comparisonFetchComplete = true,
  period,
  comparisonPeriod,
  type,
  insights,
  attributeGroups = null,
  resellerValues = null,
  resellerCommission = null,
  panelChanges = null,
  sspTypeChanges = null,
  prebidSplitMode = 'serverClient',
  login,
  onExportDataReady,
}) => {
  const theme = useTheme();
  const disabledSources = useStore(state => state.disabledSources);
  const sortByKey = useStore(state => state.sortByKey);
  const sortOrder = useStore(state => state.sortOrder);
  const fixedScale = usePersistentStore(state => state.fixedScale);
  const stickyGraphEnabled = usePersistentStore(state => state.stickyGraphEnabled);
  const stackedReportGraphHeightPx = usePersistentStore(state => state.stackedReportGraphHeightPx);
  const stackedReportGraphHidden = Boolean(
    usePersistentStore(state => state.stackedReportGraphHidden)
  );
  const {wrapperHeight, wrapperHeightSticky} = getStackedGraphLayoutHeights(
    stackedReportGraphHeightPx ?? graphSettings.wrapperHeight
  );
  const groupingEnabled = useStore(state => state.groupingEnabled);
  const centerTooltip = usePersistentStore(state => state.centerTooltip);
  const analyticsGrouping = usePersistentStore(state => state.analyticsGrouping);
  const comparisonEnabled = usePersistentStore(state => state.comparisonEnabled);
  const persistedDefaultMetric = usePersistentStore(
    state => state.stackedReportEnabledMetricDefault
  );
  const persistedAnalyticsMetric = usePersistentStore(
    state => state.stackedReportEnabledMetricAnalytics
  );
  const setPersistentState = usePersistentStore(state => state.setState);
  const disableMillify = usePersistentStore(state => state.disableMillify);
  const showNetPrebidResellerRevenue = usePersistentStore(
    state => state.showNetPrebidResellerRevenue
  );
  const prebidSspChangesEnabled = usePersistentStore(
    state => state.prebidSspChangesEnabled ?? true
  );
  const setState = useStore(state => state.setState);
  const normalizedDirectSourceSet = useMemo(() => {
    const keys = Object.keys(resellerValues || {}).map(k => String(k).toLowerCase().trim());
    return new Set([...keys, ...keys.map(k => k.replace(/s2s$/, ''))]);
  }, [resellerValues]);
  const [hoverState, setHoverState] = useState({source: null, insight: null});
  const leaveTimer = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const lastReportType = useStore(state => state.lastReportType);
  const [hideSeparateGraphs, setHideSeparateGraphs] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);
  const [isExpandedRowVisible, setIsExpandedRowVisible] = useState(false);
  const isFiltersMenuOpen = useStore(state => state.analyticsFiltersOpen);
  const expandedRowRef = useRef(null);
  const [ref, shouldGraphStick, graphWidth] = useStickyGraph(
    stickyGraphEnabled && !stackedReportGraphHidden,
    wrapperHeight,
    {
      threshold: 0.5,
    },
    hideSeparateGraphs
  );

  // Ref for table container to track scroll events
  const tableContainerRef = useRef(null);

  // Track table position to conditionally enable overflow scroll
  const shouldEnableScroll = useTableScroll(tableContainerRef, 150);

  // Keep graph sticky but fade it out when expanded row is visible (more performant)
  const graphOpacity = isExpandedRowVisible ? 0 : 1;
  const classes = useStyles({wrapperHeight, wrapperHeightSticky});

  // Force fixed scale to true when grouping is disabled
  useEffect(() => {
    if (!groupingEnabled) {
      setPersistentState('fixedScale', true);
    }
  }, [groupingEnabled, setPersistentState]);

  // Enable grouping when type is not prebid
  useEffect(() => {
    if (type !== 'prebid') {
      setState('groupingEnabled', true);
    }
  }, [type, setState]);

  // For analytics: automatically disable split (turn grouping back on)
  // when the dimension is not "bidder". Split is only valid for bidder dimension.
  useEffect(() => {
    if (type !== 'analytics') return;
    if (analyticsGrouping && analyticsGrouping !== 'bidder') {
      setState('groupingEnabled', true);
    }
  }, [type, analyticsGrouping, setState]);

  const persistedEnabledMetric =
    type === 'analytics' ? persistedAnalyticsMetric : persistedDefaultMetric;

  // rCPM is demand-only; show eCPM on prebid/direct instead.
  const enabledMetric =
    persistedEnabledMetric === 'rCPM' && type !== 'demand' ? 'eCPM' : persistedEnabledMetric;

  const setEnabledMetric = useCallback(
    metric => {
      setPersistentState(
        type === 'analytics'
          ? 'stackedReportEnabledMetricAnalytics'
          : 'stackedReportEnabledMetricDefault',
        metric
      );
    },
    [setPersistentState, type]
  );

  // Set default sorting and active metric ONLY when report type actually changes.
  useEffect(() => {
    if (lastReportType === type) return;

    if (type === 'direct') {
      setState('sortByKey', 'Demand Source Priority');
      setState('sortOrder', 'asc');
    } else if (type === 'analytics') {
      setState('sortByKey', 'TotalImpressions');
      setState('sortOrder', 'desc');
    } else {
      setState('sortByKey', 'Paid Imp');
      setState('sortOrder', 'desc');
    }

    setState('search', '');
    setState('lastReportType', type);
  }, [type, lastReportType, setState]);

  // Type can be prebid, demand, direct, or analytics
  const sourcesConfig = useMemo(() => {
    switch (type) {
      case 'prebid':
        return prebidSourcesConfig(theme, data, attributeGroups);
      case 'analytics':
        if (analyticsGrouping === 'device') {
          return deviceSourcesConfig(theme);
        }
        if (analyticsGrouping === 'country') {
          return countrySourcesConfig(theme, data);
        }
        return prebidSourcesConfig(theme, data, attributeGroups);
      case 'direct':
        return directSourcesConfig(theme);
      case 'demand':
      default:
        return demandSourcesConfig(theme);
    }
  }, [type, data, theme, attributeGroups]);

  const canApplyNetResellerRevenue =
    (type === 'prebid' || type === 'demand') &&
    login?.type === 7 &&
    showNetPrebidResellerRevenue &&
    resellerCommission != null &&
    resellerCommission !== '';

  const metricsConfig = useMemo(
    () => createMetricsConfig(theme, hideSeparateGraphs, type),
    [theme, hideSeparateGraphs, type]
  );

  const netRevenueLabelContext = useMemo(() => {
    if (!canApplyNetResellerRevenue) return null;
    return {
      stackType: type,
      directSourcesSet: normalizedDirectSourceSet,
      resellerCommission,
    };
  }, [canApplyNetResellerRevenue, type, normalizedDirectSourceSet, resellerCommission]);

  const toggleMetric = useCallback(
    metric => {
      setEnabledMetric(metric);
    },
    [setEnabledMetric]
  );

  // Carousel for analytics + demand stacked tables (shared persistence keys)
  const isColumnCarousel = type === 'analytics' || type === 'demand';
  const maxVisibleColumns = usePersistentStore(state => state.maxVisibleColumns);
  const columnsOrder = usePersistentStore(state => state.columnsOrder);
  const defaultMetrics = useMemo(() => {
    const keys = Object.keys(metricsConfig.perSource);
    if (type !== 'demand' || !Array.isArray(data) || data.length === 0) {
      return keys;
    }
    // Hide day-level Ad Requests unless the API actually returned the field
    return keys.filter(key => {
      const config = metricsConfig.perSource[key];
      if (!config?.dayLevelOnly) return true;
      return data.some(
        day => day?.[config.dataField] != null && day[config.dataField] !== ''
      );
    });
  }, [metricsConfig, type, data]);

  const allMetrics = useMemo(() => {
    if (!isColumnCarousel || !columnsOrder) return defaultMetrics;
    const validOrder = columnsOrder.filter(m => defaultMetrics.includes(m));
    const missing = defaultMetrics.filter(m => !validOrder.includes(m));
    return [...validOrder, ...missing];
  }, [isColumnCarousel, columnsOrder, defaultMetrics]);

  const handleMoveColumn = useCallback(
    (metric, direction) => {
      if (!isColumnCarousel) return;
      const currentIndex = allMetrics.indexOf(metric);
      if (currentIndex === -1) return;

      const newIndex = currentIndex + direction;
      if (newIndex < 0 || newIndex >= allMetrics.length) return;

      const newOrder = [...allMetrics];
      [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
      setPersistentState('columnsOrder', newOrder);
    },
    [isColumnCarousel, allMetrics, setPersistentState]
  );

  const {
    handlePreviousColumns,
    handleNextColumns,
    getVisibleMetrics,
    isMetricVisible,
    canNavigatePrevious,
    canNavigateNext,
    remainingLeft,
    remainingRight,
    leftMetrics,
    rightMetrics,
  } = useColumnCarousel(
    isColumnCarousel,
    allMetrics,
    maxVisibleColumns,
    enabledMetric,
    metricsConfig,
    toggleMetric
  );

  const reportData = useMemo(() => {
    if (!canApplyNetResellerRevenue) {
      return data;
    }
    return applyNetPrebidResellerRevenue(
      data,
      normalizedDirectSourceSet,
      resellerCommission,
      type
    );
  }, [data, canApplyNetResellerRevenue, resellerCommission, normalizedDirectSourceSet, type]);

  const comparisonReportData = useMemo(() => {
    if (!canApplyNetResellerRevenue || !comparisonData) {
      return comparisonData;
    }
    return applyNetPrebidResellerRevenue(
      comparisonData,
      normalizedDirectSourceSet,
      resellerCommission,
      type
    );
  }, [comparisonData, canApplyNetResellerRevenue, resellerCommission, normalizedDirectSourceSet, type]);

  const demandData = useMemo(
    () =>
      buildDemandData({
        data: reportData,
        period,
        disabledSources,
        sourcesConfig,
        metricsConfig,
        sortByKey,
        sortOrder,
        type,
        prebidSplitMode,
        directSourcesSet: normalizedDirectSourceSet,
      }),
    [
      reportData,
      period,
      disabledSources,
      sourcesConfig,
      metricsConfig,
      sortByKey,
      sortOrder,
      type,
      prebidSplitMode,
      normalizedDirectSourceSet,
    ]
  );

  // When a stacked report type loads, reset disabled sources once per type:
  // - demand: keep "Other" off by default if it exists
  // - others: no sources disabled by default
  const initialDisabledAppliedRef = useRef(false);

  // Reset the flag whenever the stacked report type changes
  useEffect(() => {
    initialDisabledAppliedRef.current = false;
  }, [type]);

  useEffect(() => {
    if (!demandData || initialDisabledAppliedRef.current) return;

    const hasOtherSource = Boolean(demandData.availableSources?.includes('Other'));

    if (type === 'demand') {
      useStore.setState({
        disabledSources: hasOtherSource ? ['Other'] : [],
      });
    } else {
      useStore.setState({
        disabledSources: [],
      });
    }

    initialDisabledAppliedRef.current = true;
  }, [type, demandData]);

  const {
    search,
    searchInput,
    setSearchInput,
    registerRowRef,
    matchedSources,
    onSearchFocus,
    onSearchBlur,
  } = useStackedReportSearch({
    tableContainerRef,
    sourcesSorted: demandData?.availableSourcesSorted,
    sourcesConfig,
  });
  const matchedSourcesSet = useMemo(() => new Set(matchedSources || []), [matchedSources]);
  const hasSearchQuery = String(search || '').trim().length > 0;
  const displayedSources = useMemo(() => {
    if (!demandData?.availableSourcesSorted) return [];
    return hasSearchQuery ? matchedSources : demandData.availableSourcesSorted;
  }, [demandData, hasSearchQuery, matchedSources]);

  const comparisonDemandData = useMemo(
    () =>
      comparisonReportData && comparisonPeriod
        ? buildDemandData({
            data: comparisonReportData,
            period: comparisonPeriod,
            disabledSources,
            sourcesConfig,
            metricsConfig,
            sortByKey,
            sortOrder,
            type,
            prebidSplitMode,
            directSourcesSet: normalizedDirectSourceSet,
          })
        : null,
    [
      comparisonReportData,
      comparisonPeriod,
      disabledSources,
      sourcesConfig,
      metricsConfig,
      sortByKey,
      sortOrder,
      type,
      prebidSplitMode,
      normalizedDirectSourceSet,
    ]
  );

  const comparisonAllowed =
    comparisonEnabled &&
    period?.label !== 'All history' &&
    !(
      type === 'analytics' &&
      (period?.label === 'realtime' ||
        period?.label === 'Last 24 hours' ||
        period?.label === 'Today')
    );

  useEffect(() => {
    if (!onExportDataReady) return;
    if (!demandData?.graphData?.length) {
      onExportDataReady(null);
      return;
    }
    onExportDataReady({
      graphData: demandData.graphData,
      metricsConfig,
      disabledSources,
      availableSourcesSorted: demandData.availableSourcesSorted,
      totals: demandData.totals,
      totalsOfEnabledSources: demandData.totalsOfEnabledSources,
      comparisonGraphData: comparisonDemandData?.graphData,
      comparisonTotals: comparisonDemandData?.totals,
      comparisonTotalsOfEnabledSources: comparisonDemandData?.totalsOfEnabledSources,
      enabledMetric,
      comparisonAvailable: comparisonAllowed && Boolean(comparisonDemandData?.graphData?.length),
    });
    return () => onExportDataReady(null);
  }, [
    demandData,
    metricsConfig,
    disabledSources,
    enabledMetric,
    comparisonDemandData,
    comparisonAllowed,
    onExportDataReady,
  ]);

  const comparisonLoading =
    comparisonAllowed && !!data && !comparisonDemandData && !comparisonFetchComplete;

  const setHover = useCallback((source, insight = null) => {
    clearTimeout(leaveTimer.current);
    clearTimeout(hoverTimeoutRef.current);

    hoverTimeoutRef.current = setTimeout(() => {
      setHoverState({source, insight});
    }, 16);
  }, []);

  const cancelHover = useCallback(() => {
    clearTimeout(hoverTimeoutRef.current);
    leaveTimer.current = setTimeout(() => {
      setHoverState({source: null, insight: null});
    }, 50);
  }, []);

  const toggleSource = useCallback(source => {
    useStore.setState(state => ({
      disabledSources: !state.disabledSources.includes(source)
        ? [...state.disabledSources, source]
        : state.disabledSources.filter(disabledSource => disabledSource !== source),
    }));
  }, []);

  const handleSortClick = useCallback(key => {
    useStore.setState(state => ({
      sortByKey: state.sortByKey === key ? state.sortByKey : key,
      sortOrder: state.sortByKey === key ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'desc',
    }));
  }, []);

  const toggleRowExpansion = useCallback(source => {
    setExpandedRow(prev => (prev === source ? null : source));
  }, []);

  useEffect(() => {
    if (!expandedRow || !expandedRowRef.current) {
      setIsExpandedRowVisible(false);
      return;
    }

    let timeoutId;
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setIsExpandedRowVisible(entry.isIntersecting);
        }, 100);
      },
      {
        threshold: 0.5,
        rootMargin: '-150px 0px 0px 0px',
      }
    );

    observer.observe(expandedRowRef.current);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [expandedRow]);

  const {
    graphChartContainerRef,
    panelChangeGuides,
    guideStripLayouts,
    panelChangeDetail,
    setPanelChangeDetail,
    openPanelChangeDetail,
    handlePrimaryAnalyticsChartInit,
    handlePanelChangeGuidesDrawn,
    handlePanelChangeZoomed,
  } = usePanelChangeGuides({
    type,
    panelChanges,
    sspTypeChanges: type === 'prebid' && prebidSspChangesEnabled ? sspTypeChanges : null,
    theme,
    period,
    data,
  });

  const splitConfig = getSplitModeConfig(prebidSplitMode);
  const splitPrimaryDataKey = splitConfig.primaryDataKey;
  const splitSecondaryDataKey = splitConfig.secondaryDataKey;
  const splitPrimaryLabel = splitConfig.primaryLabel;
  const splitSecondaryLabel = splitConfig.secondaryLabel;
  const splitPrimarySuffix = splitConfig.primarySuffix;
  const splitSecondarySuffix = splitConfig.secondarySuffix;

  const chartConfig = dataProviderKey => {
    if (!demandData) return null;

    const balloonFunction = item => {
      return createBalloonFunction(
        item,
        demandData,
        metricsConfig,
        enabledMetric,
        sourcesConfig,
        disabledSources,
        groupingEnabled,
        dataProviderKey,
        sortOrder,
        sortByKey,
        type,
        period,
        prebidSplitMode,
        normalizedDirectSourceSet,
        netRevenueLabelContext,
        theme,
        disableMillify
      );
    };

    const guides = [];
    if (hoverState.insight?.trend && hoverState.source) {
      const isGraphDataSplitSecondary = dataProviderKey === splitSecondaryDataKey;
      const isDirectHovered = normalizedDirectSourceSet.has(
        String(hoverState.source || '').toLowerCase().trim()
      );
      const isSplitSecondaryHovered =
        prebidSplitMode === 'directReseller'
          ? isDirectHovered
          : String(hoverState.source || '').toLowerCase().includes('s2s');

      if (
        (isGraphDataSplitSecondary && isSplitSecondaryHovered) ||
        (!isGraphDataSplitSecondary && !isSplitSecondaryHovered) ||
        groupingEnabled
      ) {
        const trend = hoverState.insight.trend;

        const currentPeriodColor =
          trend.direction === 'up' ? theme.palette.graphColors.revenue : 'orange';
        const previousPeriodColor =
          trend.direction === 'up' ? 'orange' : theme.palette.graphColors.revenue;

        const previousPeriod = trend.periods.period1;
        const currentPeriod = trend.periods.period2;

        guides.push({
          date: currentPeriod.start,
          toDate: moment(currentPeriod.end).add(1, 'day').format('YYYY-MM-DD'),
          fillColor: currentPeriodColor,
          fillAlpha: 0.3,
          lineAlpha: 0,
          labelRotation: 0,
          labelPosition: 'top',
          label: trend.value + '% impressions',
          color: theme.palette.text.primary,
          position: 'top',
          inside: true,
          boldLabel: true,
        });

        guides.push({
          date: previousPeriod.start,
          toDate: moment(previousPeriod.end).add(1, 'day').format('YYYY-MM-DD'),
          fillColor: previousPeriodColor,
          fillAlpha: 0.3,
          lineAlpha: 0,
          labelRotation: 0,
          labelPosition: 'top',
          color: theme.palette.text.primary,
          position: 'top',
          inside: true,
          boldLabel: true,
        });
      }
    }

    const primaryDataProviderKey = groupingEnabled ? 'graphData' : splitPrimaryDataKey;
    const isPrimaryChart = dataProviderKey === primaryDataProviderKey;

    if (
      (type === 'analytics' || (type === 'prebid' && prebidSspChangesEnabled)) &&
      isPrimaryChart &&
      panelChangeGuides.length > 0
    ) {
      guides.push(...panelChangeGuides);
    }

    const isLast24Hours =
      period?.label === 'Last 24 hours' ||
      period?.label === 'Today' ||
      period?.label === 'realtime';
    const isRealtime = period?.label === 'realtime';

    return {
      columnSpacing: 0,
      zoomOutOnDataUpdate: true,
      type: 'serial',
      theme: 'light',
      marginTop: 15,
      marginRight: 15,
      responsive: {
        enabled: true,
      },
      dataProvider: demandData[dataProviderKey],
      categoryField: 'date',
      categoryAxis: {
        tickLength: 1,
        labelRotation: 45,
        color: theme.palette.graphColors.themeGray,
        axisColor: theme.palette.graphColors.themeLightGrayOpaque,
        gridAlpha: 0,
        guides,
        labelFunction: (valueText, serialDataItem) => {
          const timestamp = serialDataItem?.dataContext?.date;
          const timeMoment = moment(timestamp);
          if (!timeMoment.isValid()) return valueText;

          if (isRealtime) {
            return timeMoment.format('HH:mm:ss');
          } else if (isLast24Hours) {
            return timeMoment.format('HH:mm');
          } else {
            return timeMoment.format('MMM D');
          }
        },
      },
      valueAxes: getValueAxisConfig(
        metricsConfig,
        shouldGraphStick,
        theme,
        fixedScale,
        demandData,
        sourcesConfig,
        groupingEnabled,
        dataProviderKey
      ),
      graphs: getGraphsConfig(
        demandData,
        sourcesConfig,
        theme,
        hoverState.source,
        disabledSources,
        enabledMetric,
        hideSeparateGraphs,
        fixedScale,
        balloonFunction,
        metricsConfig,
        type
      ),
      chartCursor: {
        oneBalloonOnly: true,
        cursorColor: theme.palette.graphColors.themeGray,
        color: 'rgba(0,0,0,0)',
        categoryBalloonColor: 'rgba(0,0,0,0)',
        categoryBalloonAlpha: 1,
        cursorAlpha: 1,
        valueLineEnabled: true,
        valueLineBalloonEnabled: true,
        valueLineAlpha: 1,
        zoomable: false,
        selectWithoutZooming: true,
        categoryBalloonEnabled: true,
        balloonPointerOrientation: centerTooltip ? 'vertical' : 'horizontal',
      },
      chartScrollbar: {
        enabled: false,
        backgroundAlpha: 0,
        dragIcon: 'dragIconRectSmall',
        graph: 'g1',
        graphFillAlpha: 0,
        graphLineAlpha: 0,
        scrollbarHeight: 30,
        color: theme.palette.primary.main,
        graphLineColor: theme.palette.graphColors.themeAlmostWhite,
        selectedGraphFillColor: theme.palette.graphColors.themeAlmostWhite,
        selectedGraphFillAlpha: 0,
        graphType: 'step',
        selectedBackgroundAlpha: 0.1,
        selectedBackgroundColor: theme.palette.graphColors.themeAlmostWhite,
      },
      creditsPosition: 'bottom-right',
      zoomOutButton: {
        backgroundColor: '#e0e0e0',
        backgroundAlpha: 0,
      },
      zoomOutText: '',
      ...((type === 'analytics' || (type === 'prebid' && prebidSspChangesEnabled)) &&
        isPrimaryChart && {
          listeners: [
            {event: 'init', method: handlePrimaryAnalyticsChartInit},
            {event: 'drawn', method: handlePanelChangeGuidesDrawn},
            {event: 'zoomed', method: handlePanelChangeZoomed},
          ],
        }),
    };
  };

  if (!demandData) return null;

  const isAnalyticsBidderDimension =
    type === 'analytics' && (analyticsGrouping === 'bidder' || !analyticsGrouping);

  const shouldRenderServerClientSplitChart =
    !groupingEnabled && (type === 'prebid' || isAnalyticsBidderDimension);

  /** Offset second chart above the first; must track dynamic graph height (not fixed 210/170). */
  const splitChartSecondBottom = shouldGraphStick ? wrapperHeightSticky / 2 : wrapperHeight / 2;

  return (
    <>
      {!stackedReportGraphHidden && (
        <div className={classes.graphWrapper} ref={ref}>
          <div
            ref={graphChartContainerRef}
            style={{
              width: graphWidth,
              opacity: graphOpacity,
              transition: 'opacity 0.3s ease-in-out',
              pointerEvents: isExpandedRowVisible ? 'none' : 'auto',
            }}
            className={`${classes.chartContainer} ${
              shouldGraphStick ? classes.fixedPositionWrapper : ''
            } ${panelChangeDetail ? 'panel-change-balloons-hidden' : ''}`}
          >
            <AmCharts.React
              style={{
                width: groupingEnabled ? graphWidth : `${parseInt(graphWidth) + 10}px`,
                // Suppress the chart cursor/tooltip while the change log is open.
                pointerEvents: panelChangeDetail ? 'none' : 'auto',
              }}
              options={chartConfig(groupingEnabled ? 'graphData' : splitPrimaryDataKey)}
              className={`${groupingEnabled ? classes.graph : classes.graphUngrouped} ${
                shouldGraphStick
                  ? groupingEnabled
                    ? classes.fixedPositionGraph
                    : classes.fixedPositionGraphUngrouped
                  : ''
              }`}
            />

            {panelChangeDetail && type === 'analytics' && (
              <PanelChangeDetailPanel
                date={panelChangeDetail.date}
                changes={panelChanges?.changesByDate?.[panelChangeDetail.date] || []}
                period={period}
                anchorRect={panelChangeDetail.anchorRect}
                containerRef={graphChartContainerRef}
                onClose={() => setPanelChangeDetail(null)}
              />
            )}

            {panelChangeDetail && type === 'prebid' && prebidSspChangesEnabled && (
              <SspTypeChangePopup
                date={panelChangeDetail.date}
                changes={sspTypeChanges?.changesByDate?.[panelChangeDetail.date] || []}
                period={period}
                anchorRect={panelChangeDetail.anchorRect}
                containerRef={graphChartContainerRef}
                attributeGroups={attributeGroups}
                onClose={() => setPanelChangeDetail(null)}
              />
            )}

            {shouldRenderServerClientSplitChart && (
              <AmCharts.React
                style={{
                  width: graphWidth,
                  bottom: splitChartSecondBottom,
                }}
                options={chartConfig(splitSecondaryDataKey)}
                className={`${classes.graphUngrouped} ${
                  shouldGraphStick ? classes.fixedPositionGraphUngrouped : ''
                }`}
              />
            )}

            {((type === 'analytics' || (type === 'prebid' && prebidSspChangesEnabled)) &&
              guideStripLayouts.length > 0) && (
              <PanelChangeGuideStrips
                strips={guideStripLayouts}
                onStripClick={openPanelChangeDetail}
                period={period}
                formatTooltip={
                  type === 'prebid'
                    ? strip =>
                        formatSspTypeChangeBadgeTooltip(
                          sspTypeChanges?.changesByDate?.[strip.date],
                          strip.date,
                          period
                        )
                    : undefined
                }
              />
            )}

          </div>
        </div>
      )}

      {type === 'analytics' && (
        <Grid container spacing={2} style={{marginTop: 8, marginBottom: 24}}>
          <Grid item xs={12}>
            <AnalyticsFilters />
          </Grid>
        </Grid>
      )}

      <TableContainer
        ref={tableContainerRef}
        className={`${classes.table} ${shouldEnableScroll ? classes.tableContainerScroll : classes.tableContainerHidden}`}
        style={{
          maxHeight: 'calc(100vh - 110px)',
          paddingTop: 0,
          opacity: isFiltersMenuOpen ? 0.3 : 1,
          transition: 'opacity 120ms ease',
        }}
      >
        <Table className={classes.table} component={Paper}>
          <TableHead
            style={{
              position: 'sticky',
              top: -1,
              zIndex: 10,
              boxShadow: '0 2px 24px rgba(0, 0, 0, 0.15)',
            }}
          >
            <TableRow>
              <StyledTableCell
                align="left"
                style={{
                  background: theme.palette.background.paper,
                  height: 90,
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  borderRight: isColumnCarousel ? `1px dashed ${theme.palette.divider}` : undefined,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 48,
                    position: 'relative',
                  }}
                >
                  <Tooltip title={disabledSources.length === 0 ? 'Disable all' : 'Enable all'}>
                    <Checkbox
                      style={{marginLeft: 8, marginRight: 8}}
                      checked={disabledSources.length === 0}
                      indeterminate={
                        disabledSources.length > 0 &&
                        disabledSources.length < (demandData?.availableSources?.length || 0)
                      }
                      onChange={() => {
                        if (disabledSources.length === 0) {
                          setState('disabledSources', demandData.availableSources);
                        } else {
                          setState('disabledSources', []);
                        }
                      }}
                      color="primary"
                    />
                  </Tooltip>
                  <div
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'left',
                    }}
                  >
                    {type === 'direct' && (
                      <Tooltip
                        title={`Sort by ${sortOrder !== 'asc' ? 'ascending' : 'descending'} line item priority`}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleSortClick('Demand Source Priority')}
                          style={{marginRight: 8}}
                        >
                          {sortByKey === 'Demand Source Priority' ? (
                            sortOrder === 'asc' ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )
                          ) : (
                            <ExpandMore style={{opacity: 0.3}} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    {type !== 'direct' && type === 'analytics' && (
                      <FormControl
                        variant="outlined"
                        size="small"
                        style={{minWidth: 160, marginLeft: 8}}
                      >
                        <InputLabel id="analytics-dimension-label">Dimension</InputLabel>
                        <Select
                          labelId="analytics-dimension-label"
                          value={analyticsGrouping || 'demandSource'}
                          onChange={e => {
                            usePersistentStore
                              .getState()
                              .setState('analyticsGrouping', e.target.value);
                          }}
                          label="Dimension"
                        >
                          <MenuItem value="bidder">Bidder</MenuItem>
                          <MenuItem value="country">Countries</MenuItem>
                          <MenuItem value="device">Devices</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {type !== 'direct' && type !== 'analytics' && <span>Demand source</span>}
                    {type === 'direct' && <span>Line item</span>}
                  </div>
                </div>
              </StyledTableCell>
              {isColumnCarousel && (
                <CarouselNavigationCell
                  direction="left"
                  onClick={handlePreviousColumns}
                  disabled={!canNavigatePrevious}
                  theme={theme}
                  remainingCount={remainingLeft}
                  metricKeys={leftMetrics}
                  metricsConfig={metricsConfig}
                />
              )}
              {(isColumnCarousel ? getVisibleMetrics() : allMetrics).map(metric => (
                <MetricHeader
                  key={metric}
                  metric={metric}
                  metricsConfig={metricsConfig}
                  enabledMetric={enabledMetric}
                  toggleMetric={toggleMetric}
                  handleSortClick={handleSortClick}
                  sortByKey={sortByKey}
                  sortOrder={sortOrder}
                  hideSeparateGraphs={hideSeparateGraphs}
                  setHideSeparateGraphs={setHideSeparateGraphs}
                  theme={theme}
                  type={type}
                  isVisible={!isColumnCarousel || isMetricVisible(metric)}
                  allMetrics={allMetrics}
                  onMoveColumn={isColumnCarousel ? handleMoveColumn : null}
                />
              ))}
              {isColumnCarousel && (
                <CarouselNavigationCell
                  direction="right"
                  onClick={handleNextColumns}
                  disabled={!canNavigateNext}
                  theme={theme}
                  remainingCount={remainingRight}
                  metricKeys={rightMetrics}
                  metricsConfig={metricsConfig}
                />
              )}
            </TableRow>
            {/* Search and totals row */}
            <TableRow style={{top: -1, position: 'relative'}}>
              <StyledTableCell
                align="center"
                style={{
                  backgroundColor: theme.palette.background.paper,
                  borderRight: isColumnCarousel ? `1px solid ${theme.palette.divider}` : undefined,
                }}
              >
                {(demandData?.availableSources?.length || 0) > 8 ? (
                  <TextField
                    label="Search"
                    size="small"
                    style={{width: '90%'}}
                    value={searchInput}
                    onFocus={onSearchFocus}
                    onBlur={onSearchBlur}
                    onChange={e => {
                      setSearchInput(e.target.value);
                    }}
                  />
                ) : (
                  <Typography
                    variant={'body2'}
                    color={'textSecondary'}
                    style={{fontWeight: 'bold', textAlign: 'left', paddingLeft: 20}}
                  >
                    Total
                  </Typography>
                )}
              </StyledTableCell>
              {isColumnCarousel && <CarouselNavigationCell isEmpty theme={theme} />}
              <TableTotals
                metricsConfig={metricsConfig}
                demandData={demandData}
                comparisonDemandData={comparisonDemandData}
                comparisonEnabled={comparisonAllowed}
                comparisonLoading={comparisonLoading}
                enabledMetric={enabledMetric}
                toggleMetric={toggleMetric}
                theme={theme}
                disableMillify={disableMillify}
                visibleMetrics={isColumnCarousel ? getVisibleMetrics() : undefined}
                isMetricVisible={isColumnCarousel ? isMetricVisible : undefined}
              />
              {isColumnCarousel && <CarouselNavigationCell isEmpty theme={theme} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {!groupingEnabled && (
              <>
                <TableRow>
                  <StyledTableCell
                    className="splitLabelCell"
                    align="left"
                    style={{
                      borderRight: isColumnCarousel
                        ? `1px solid ${theme.palette.divider}`
                        : undefined,
                    }}
                  >
                    <Typography
                      variant={'body2'}
                      color={'textSecondary'}
                      style={{fontWeight: 'bold'}}
                    >
                      {splitSecondaryLabel}
                    </Typography>
                  </StyledTableCell>
                  {isColumnCarousel && <CarouselNavigationCell isEmpty theme={theme} />}
                  <TableTotals
                    metricsConfig={metricsConfig}
                    demandData={demandData}
                    comparisonDemandData={comparisonDemandData}
                    comparisonEnabled={comparisonAllowed}
                    comparisonLoading={comparisonLoading}
                    enabledMetric={enabledMetric}
                    toggleMetric={toggleMetric}
                    theme={theme}
                    disableMillify={disableMillify}
                    totalsSuffix={splitSecondarySuffix}
                    visibleMetrics={isColumnCarousel ? getVisibleMetrics() : undefined}
                    isMetricVisible={isColumnCarousel ? isMetricVisible : undefined}
                  />
                  {isColumnCarousel && <CarouselNavigationCell isEmpty theme={theme} />}
                </TableRow>
                <TableRow>
                  <StyledTableCell
                    className="splitLabelCell"
                    align="left"
                    style={{
                      borderRight: isColumnCarousel
                        ? `1px solid ${theme.palette.divider}`
                        : undefined,
                    }}
                  >
                    <Typography
                      variant={'body2'}
                      color={'textSecondary'}
                      style={{fontWeight: 'bold'}}
                    >
                      {splitPrimaryLabel}
                    </Typography>
                  </StyledTableCell>
                  {isColumnCarousel && <CarouselNavigationCell isEmpty theme={theme} />}
                  <TableTotals
                    metricsConfig={metricsConfig}
                    demandData={demandData}
                    comparisonDemandData={comparisonDemandData}
                    comparisonEnabled={comparisonAllowed}
                    comparisonLoading={comparisonLoading}
                    enabledMetric={enabledMetric}
                    toggleMetric={toggleMetric}
                    theme={theme}
                    disableMillify={disableMillify}
                    totalsSuffix={splitPrimarySuffix}
                    visibleMetrics={isColumnCarousel ? getVisibleMetrics() : undefined}
                    isMetricVisible={isColumnCarousel ? isMetricVisible : undefined}
                  />
                  {isColumnCarousel && <CarouselNavigationCell isEmpty theme={theme} />}
                </TableRow>
              </>
            )}

            {displayedSources.map(source => (
              <React.Fragment key={source}>
                <SourceRow
                  resellerValue={resellerValues?.[source.toLowerCase()]}
                  insights={insights}
                  shouldGraphStick={
                    shouldGraphStick && !isExpandedRowVisible && !stackedReportGraphHidden
                  }
                  stickyGraphRegionHeightPx={stackedReportGraphHidden ? 0 : wrapperHeightSticky}
                  source={source}
                  isSearchMatch={matchedSourcesSet.has(source)}
                  sourcesConfig={sourcesConfig}
                  metricsConfig={metricsConfig}
                  demandData={demandData}
                  comparisonDemandData={comparisonDemandData}
                  comparisonEnabled={comparisonAllowed}
                  comparisonLoading={comparisonLoading}
                  disabledSources={disabledSources}
                  enabledMetric={enabledMetric}
                  toggleSource={toggleSource}
                  toggleMetric={toggleMetric}
                  setHover={setHover}
                  cancelHover={cancelHover}
                  hoveredSource={hoverState.source}
                  classes={classes}
                  theme={theme}
                  disableMillify={disableMillify}
                  scrollContainerRef={tableContainerRef}
                  registerRowRef={registerRowRef}
                  isPrebid={type === 'prebid'}
                  isExpanded={expandedRow === source}
                  onToggleExpansion={() => toggleRowExpansion(source)}
                  visibleMetrics={isColumnCarousel ? getVisibleMetrics() : undefined}
                  isMetricVisible={isColumnCarousel ? isMetricVisible : undefined}
                  showNavigationCells={isColumnCarousel}
                  reportType={type}
                  netRevenueLabelContext={netRevenueLabelContext}
                />

                {type === 'prebid' && (
                  <TableRow>
                    <StyledTableCell
                      style={{paddingBottom: 0, paddingTop: 0}}
                      colSpan={
                        (isColumnCarousel ? getVisibleMetrics().length : allMetrics.length) +
                        1 +
                        (isColumnCarousel ? 2 : 0)
                      }
                    >
                      <Collapse in={expandedRow === source} timeout="auto" unmountOnExit>
                        <Box margin={2} ref={expandedRow === source ? expandedRowRef : null}>
                          <PricesReport login={login} selectedBidder={source} hideControls={true} />
                        </Box>
                      </Collapse>
                    </StyledTableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {shouldGraphStick && !stackedReportGraphHidden && (
              <TableRow>
                <StyledTableCell
                  colSpan={
                    (isColumnCarousel ? getVisibleMetrics().length : allMetrics.length) +
                    1 +
                    (isColumnCarousel ? 2 : 0)
                  }
                  style={{
                    height: wrapperHeightSticky,
                    padding: 0,
                    border: 'none',
                  }}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default React.memo(StackedReportGraph);
